import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Calendar, Gift, Activity, Users, ChevronRight, ChevronLeft, Loader2, X, ZoomIn } from 'lucide-react';
import { useTranslations } from '../context/TranslationsContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Fallback instructors if API fails
const fallbackInstructors = [
  {
    id: 'INS001',
    name: 'Gerard Tarka',
    specialization: { pl: 'BMX', en: 'BMX', de: 'BMX', uk: 'BMX' },
    disciplines: ['BMX'],
    bio: {
      pl: 'Profesjonalny rider BMX z 10-letnim doświadczeniem. Wielokrotny medalista zawodów krajowych i międzynarodowych.',
      en: 'Professional BMX rider with 10 years of experience. Multiple medalist in national and international competitions.'
    },
    image: 'https://images.unsplash.com/photo-1568602471122-7832951cc4c5?auto=compress&w=400'
  },
  {
    id: 'INS002',
    name: 'Kamil Nowak',
    specialization: { pl: 'Hulajnoga', en: 'Scooter', de: 'Roller', uk: 'Самокат' },
    disciplines: ['HULAJNOGA'],
    bio: {
      pl: 'Mistrz Polski w freestyle scooter. Specjalizuje się w nauczaniu dzieci i młodzieży.',
      en: 'Polish champion in freestyle scooter. Specializes in teaching children and youth.'
    },
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=compress&w=400'
  },
  {
    id: 'INS003',
    name: 'Anna Kowalska',
    specialization: { pl: 'Deskorolka', en: 'Skateboard', de: 'Skateboard', uk: 'Скейтборд' },
    disciplines: ['DESKOROLKA'],
    bio: {
      pl: 'Instruktorka z certyfikatem ISA. 8 lat doświadczenia w nauczaniu wszystkich poziomów zaawansowania.',
      en: 'ISA certified instructor. 8 years of experience teaching all skill levels.'
    },
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=compress&w=400'
  }
];

// Fallback gallery
const fallbackGallery = [
  'https://images.unsplash.com/photo-1636715083006-a494358bb99a?auto=compress&w=600',
  'https://images.pexels.com/photos/17370164/pexels-photo-17370164.jpeg?auto=compress&w=600',
  'https://images.unsplash.com/photo-1696616297091-c07da52da503?auto=compress&w=600',
  'https://images.unsplash.com/photo-1572848781041-8e958e3fb379?auto=compress&w=600',
  'https://images.unsplash.com/photo-1763044939081-e2c29ffddefd?auto=compress&w=600',
  'https://images.pexels.com/photos/12580332/pexels-photo-12580332.jpeg?auto=compress&w=600'
];

// Discipline translations mapping (fallback when DB translations unavailable)
const DISCIPLINE_TRANSLATIONS = {
  'BMX': { pl: 'BMX', en: 'BMX', de: 'BMX', uk: 'BMX' },
  'DESKOROLKA': { pl: 'Deskorolka', en: 'Skateboard', de: 'Skateboard', uk: 'Скейтборд' },
  'HULAJNOGA': { pl: 'Hulajnoga', en: 'Scooter', de: 'Roller', uk: 'Самокат' },
  'ROLKI': { pl: 'Rolki', en: 'Inline Skates', de: 'Inlineskates', uk: 'Ролики' },
  'GOKART': { pl: 'Gokart', en: 'Go-kart', de: 'Go-Kart', uk: 'Картинг' }
};

// Map FitNet discipline names to DB translation keys (discover.*.title)
const DISCIPLINE_TO_DB_KEY = {
  'BMX': 'discover.bmx.title',
  'HULAJNOGA': 'discover.scooter.title',
  'DESKOROLKA': 'discover.skateboard.title',
  'ROLKI': 'discover.rollerblades.title',
  'GOKART': 'discover.gokart.title'
};

const ClassesPage = () => {
  const { t: ti18n, i18n } = useTranslation();
  const { td: tCms } = useTranslations(['activities', 'common']);
  const lang = i18n.language?.split('-')[0] || 'pl';
  
  // Dynamic data from API - store raw data
  const [rawInstructors, setRawInstructors] = useState(null);
  const [gallery, setGallery] = useState(fallbackGallery);
  const [loading, setLoading] = useState(true);
  
  // Gallery lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Gallery lightbox functions
  const openLightbox = useCallback((index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const nextImage = useCallback(() => {
    setLightboxIndex((prev) => (prev + 1) % gallery.length);
  }, [gallery.length]);

  const prevImage = useCallback(() => {
    setLightboxIndex((prev) => (prev - 1 + gallery.length) % gallery.length);
  }, [gallery.length]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      
      switch (e.key) {
        case 'ArrowRight':
          nextImage();
          break;
        case 'ArrowLeft':
          prevImage();
          break;
        case 'Escape':
          closeLightbox();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, nextImage, prevImage, closeLightbox]);
  
  // Fetch data once on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [instructorsRes, galleryRes] = await Promise.all([
          axios.get(`${API}/api/classes/instructors`).catch(() => ({ data: [] })),
          axios.get(`${API}/api/classes/gallery`).catch(() => ({ data: [] }))
        ]);
        
        if (instructorsRes.data.length > 0) {
          // Store raw data - filter only active with names
          const filtered = instructorsRes.data.filter(inst => inst.isActive !== false && inst.name);
          if (filtered.length > 0) {
            setRawInstructors(filtered);
          }
        }
        
        if (galleryRes.data.length > 0) {
          const galleryUrls = galleryRes.data.map(img => {
            const url = img.url || img;
            return url.startsWith('/') ? `${API}${url}` : url;
          });
          setGallery(galleryUrls);
        }
      } catch (error) {
        console.error('Error fetching activities data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);
  
  // Translation helper
  const t = (key, fallback) => {
    const cmsValue = tCms(key);
    if (cmsValue && cmsValue !== key) return cmsValue;
    const i18nVal = ti18n(key);
    if (i18nVal && i18nVal !== key) return i18nVal;
    return fallback || key;
  };

  // Get translated discipline name
  const getTranslatedDiscipline = (discipline) => {
    if (!discipline) return '';
    const upperDisc = discipline.toUpperCase();
    
    // Priority 1: Try DB translation via discover.*.title keys
    const dbKey = DISCIPLINE_TO_DB_KEY[upperDisc];
    if (dbKey) {
      const dbTranslation = tCms(dbKey);
      if (dbTranslation && dbTranslation !== dbKey) {
        return dbTranslation;
      }
    }
    
    // Priority 2: Try hardcoded translations for current language
    if (DISCIPLINE_TRANSLATIONS[upperDisc] && DISCIPLINE_TRANSLATIONS[upperDisc][lang]) {
      return DISCIPLINE_TRANSLATIONS[upperDisc][lang];
    }
    
    // Priority 3: Capitalize original
    return discipline.charAt(0).toUpperCase() + discipline.slice(1).toLowerCase();
  };

  // Process instructors with current language - this runs on every render when lang changes
  const instructors = (rawInstructors || fallbackInstructors).map(inst => {
    // Get discipline display text(s)
    let disciplineDisplay = [];

    // Priority 1: disciplines array - translate using DB/map
    if (Array.isArray(inst.disciplines) && inst.disciplines.length > 0) {
      disciplineDisplay = inst.disciplines.map(d => getTranslatedDiscipline(d));
    }

    // Priority 2: specialization with EXACT language match only
    if (disciplineDisplay.length === 0 && inst.specialization && typeof inst.specialization === 'object' && inst.specialization[lang]) {
      disciplineDisplay = [inst.specialization[lang]];
    }

    // Priority 3: any available specialization (last resort, better than empty)
    if (disciplineDisplay.length === 0 && inst.specialization && typeof inst.specialization === 'object') {
      const anyLang = Object.values(inst.specialization).find(v => v);
      if (anyLang) disciplineDisplay = [anyLang];
    }
    
    // Get bio with language
    let bio = '';
    if (inst.bio) {
      if (typeof inst.bio === 'object') {
        bio = inst.bio[lang] || inst.bio.pl || inst.bio.en || '';
      } else {
        bio = inst.bio;
      }
    }
    
    // Get image URL
    let image = inst.image || inst.avatar || 'https://images.unsplash.com/photo-1568602471122-7832951cc4c5?auto=compress&w=400';
    if (image.startsWith('/')) {
      image = `${API}${image}`;
    }
    
    return {
      id: inst._id || inst.id,
      name: inst.name,
      disciplineDisplay,
      bio,
      image
    };
  });

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="classes-page">
      {/* Hero */}
      <section className="relative h-[40vh] mb-16">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src="/videos/classes-hero.webm"
          poster="/videos/classes-hero.jpg"
          muted
          autoPlay
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-background" />
        <div className="relative z-10 h-full flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center px-6"
          >
            <h1 className="text-5xl md:text-6xl font-['Syne'] font-bold text-white mb-4">
              {t('activities.classesTitle')}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Description */}
      <section className="px-6 md:px-12 max-w-5xl mx-auto mb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-card p-8 md:p-12"
        >
          <h2 className="text-3xl font-['Syne'] font-bold mb-6">
            {t('activities.classesForEveryone', 'Classes for everyone')}
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed mb-6">
            {t('activities.classesDescription', 'We offer group and individual classes for all skill levels. Our qualified instructors will help you master the basics or develop advanced skills.')}
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-4 bg-white/5 rounded-xl">
              <h3 className="font-semibold mb-2">{t('activities.groupClasses', 'Group classes')}</h3>
              <p className="text-muted-foreground text-sm">
                {t('activities.groupClassesInfo', 'Max 8 people, 60 min, from 80 PLN')}
              </p>
            </div>
            <div className="p-4 bg-white/5 rounded-xl">
              <h3 className="font-semibold mb-2">{t('activities.individualClasses', 'Individual classes')}</h3>
              <p className="text-muted-foreground text-sm">
                {t('activities.individualClassesInfo', '1-on-1 with instructor, 60 min, from 150 PLN')}
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Instructors */}
      <section className="px-6 md:px-12 max-w-7xl mx-auto mb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-['Syne'] font-bold mb-4">{t('activities.instructors')}</h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {instructors.map((instructor, index) => (
            <motion.div
              key={`${instructor.id}-${lang}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="glass-card overflow-hidden"
              data-testid={`instructor-${instructor.id}`}
            >
              <div className="aspect-square overflow-hidden">
                <img
                  src={instructor.image}
                  alt={instructor.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                  {instructor.disciplineDisplay?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {instructor.disciplineDisplay.map((discipline, idx) => (
                      <span
                        key={`${instructor.id}-discipline-${idx}`}
                        className="inline-block px-3 py-1 text-xs font-medium text-white uppercase rounded-full"
                        style={{ backgroundColor: '#7C3AED' }}
                      >
                        {discipline}
                      </span>
                    ))}
                  </div>
                )}
                <h3 className="text-xl font-bold mb-3">{instructor.name}</h3>
                <p className="text-muted-foreground text-sm">{instructor.bio}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Gallery */}
      <section className="px-6 md:px-12 max-w-7xl mx-auto mb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-['Syne'] font-bold mb-4">{t('activities.gallery')}</h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {gallery.map((img, index) => (
            <motion.button
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="aspect-square rounded-2xl overflow-hidden relative group"
              onClick={() => openLightbox(index)}
              data-testid={`gallery-image-${index}`}
            >
              <img src={img} alt={`Gallery ${index + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <ZoomIn className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </motion.button>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 max-w-4xl mx-auto text-center">
        <Link
          to="/book"
          className="btn-primary text-lg px-10 py-4 inline-flex items-center gap-2"
          data-testid="classes-cta"
        >
          {t('common.bookNow')}
          <ArrowRight className="w-5 h-5" />
        </Link>
      </section>

      {/* Fullscreen Gallery Lightbox */}
      <AnimatePresence>
        {lightboxOpen && gallery.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={closeLightbox}
          >
            {/* Close button */}
            <button
              className="absolute top-4 right-4 z-10 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors"
              onClick={closeLightbox}
            >
              <X className="w-8 h-8" />
            </button>

            {/* Image counter */}
            <div className="absolute top-4 left-4 z-10 px-4 py-2 bg-black/50 rounded-full text-white/80 text-sm font-medium">
              {lightboxIndex + 1} / {gallery.length}
            </div>

            {/* Previous button */}
            {gallery.length > 1 && (
              <button
                className="absolute left-4 z-10 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
              >
                <ChevronLeft className="w-10 h-10" />
              </button>
            )}

            {/* Main image */}
            <motion.div
              key={lightboxIndex}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="max-w-[90vw] max-h-[90vh] flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={gallery[lightboxIndex]}
                alt={`Gallery ${lightboxIndex + 1}`}
                className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              />
            </motion.div>

            {/* Next button */}
            {gallery.length > 1 && (
              <button
                className="absolute right-4 z-10 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
              >
                <ChevronRight className="w-10 h-10" />
              </button>
            )}

            {/* Thumbnail strip */}
            {gallery.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2 px-4 py-3 bg-black/50 backdrop-blur-sm rounded-xl max-w-[90vw] overflow-x-auto">
                {gallery.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightboxIndex(idx);
                    }}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all ${
                      idx === lightboxIndex 
                        ? 'ring-2 ring-primary scale-105' 
                        : 'opacity-50 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={img}
                      alt={`Thumbnail ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const BirthdaysPage = () => {
  const { t: ti18n, i18n } = useTranslation();
  const { td: tCms } = useTranslations(['activities', 'common']);
  const lang = i18n.language?.split('-')[0] || 'pl';
  
  // Translation helper
  const t = (key, fallback) => {
    const cmsValue = tCms(key);
    if (cmsValue && cmsValue !== key) return cmsValue;
    const i18nVal = ti18n(key);
    if (i18nVal && i18nVal !== key) return i18nVal;
    return fallback || key;
  };

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="birthdays-page">
      {/* Hero */}
      <section className="relative h-[40vh] mb-16">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src="/videos/birthday-hero.webm"
          poster="/videos/birthday-hero.jpg"
          muted
          autoPlay
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-background" />
        <div className="relative z-10 h-full flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center px-6"
          >
            <h1 className="text-5xl md:text-6xl font-['Syne'] font-bold text-white mb-4">
              {t('nav.birthdays')}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Description */}
      <section className="px-6 md:px-12 max-w-5xl mx-auto mb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-card p-8 md:p-12"
        >
          <h2 className="text-3xl font-['Syne'] font-bold mb-6">
            {t('activities.unforgettableBirthdays', 'Unforgettable birthdays')}
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed mb-8">
            {t('activities.birthdaysDescription', 'Organize amazing birthdays at AvePark! The birthday package includes 2 hours of riding, an instructor, a relaxation zone, and a cake. Perfect for kids and teens who love extreme sports.')}
          </p>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="text-center p-6 bg-white/5 rounded-xl">
              <Gift className="w-10 h-10 text-primary mx-auto mb-4" />
              <h3 className="font-semibold mb-2">{t('activities.upTo10People', 'Up to 10 people')}</h3>
              <p className="text-sm text-muted-foreground">
                {t('activities.perfectBirthdayGroup', 'Perfect birthday group size')}
              </p>
            </div>
            <div className="text-center p-6 bg-white/5 rounded-xl">
              <Calendar className="w-10 h-10 text-primary mx-auto mb-4" />
              <h3 className="font-semibold mb-2">{t('activities.packageTime', '2h')}</h3>
              <p className="text-sm text-muted-foreground">
                {t('activities.packageDuration', 'Package duration')}
              </p>
            </div>
            <div className="text-center p-6 bg-white/5 rounded-xl">
              <Users className="w-10 h-10 text-primary mx-auto mb-4" />
              <h3 className="font-semibold mb-2">{t('activities.instructor', 'Instructor')}</h3>
              <p className="text-sm text-muted-foreground">
                {t('activities.dedicatedSupervisor', 'Dedicated supervisor')}
              </p>
            </div>
          </div>

          <p className="text-3xl font-bold text-primary text-center mb-8">
            {t('activities.fromPrice', 'from 500 PLN')}
          </p>

          <div className="text-center">
            <Link
              to="https://urodziny.avepark.pl/"
              target="_blank"
              className="btn-primary text-lg px-10 py-4 inline-flex items-center gap-2"
              data-testid="birthdays-cta"
            >
              {t('activities.bookBirthdayParty', 'Book birthday party')}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

const PhysioPage = () => {
  const { t: ti18n, i18n } = useTranslation();
  const { td: tCms } = useTranslations(['activities', 'common']);
  const lang = i18n.language?.split('-')[0] || 'pl';
  
  // Translation helper
  const t = (key, fallback) => {
    const cmsValue = tCms(key);
    if (cmsValue && cmsValue !== key) return cmsValue;
    const i18nVal = ti18n(key);
    if (i18nVal && i18nVal !== key) return i18nVal;
    return fallback || key;
  };

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="physio-page">
      {/* Hero */}
      <section className="relative h-[40vh] mb-16">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src="/videos/physio-hero.webm"
          poster="/videos/physio-hero.jpg"
          muted
          autoPlay
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-background" />
        <div className="relative z-10 h-full flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center px-6"
          >
            <h1 className="text-5xl md:text-6xl font-['Syne'] font-bold text-white mb-4">
              {t('nav.physio')}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Description */}
      <section className="px-6 md:px-12 max-w-5xl mx-auto mb-16">
        <div className="grid md:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8"
          >
            <Activity className="w-12 h-12 text-primary mb-6" />
            <h2 className="text-2xl font-bold mb-4">
              {t('activities.sportsPhysiotherapy', 'Sports physiotherapy')}
            </h2>
            <p className="text-muted-foreground mb-6">
              {t('activities.physioDescription', 'Our team of physiotherapists specializes in working with extreme sports athletes. We offer injury rehabilitation, competition preparation, and regular prevention.')}
            </p>
            <Link
              to="https://www.znanylekarz.pl/michal-popowski-2/fizjoterapeuta/nowa-iwiczna"
              className="btn-primary inline-flex items-center gap-2"
              target="_blank"
              data-testid="physio-cta"
            >
              {t('activities.bookAppointment', 'Book appointment')}
              <ChevronRight className="w-5 h-5" />
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="glass-card overflow-hidden"
          >
            <div className="aspect-square">
              <img
                src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=compress&w=600"
                alt="Physiotherapist"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-6">
              <span className="text-primary text-sm font-medium">
                {t('activities.ourSpecialist', 'Our specialist')}
              </span>
              <h3 className="text-xl font-bold mt-1 mb-2">Mgr Michał Popowski</h3>
              <p className="text-muted-foreground text-sm">
                {t('activities.physioSpecialistBio', 'Sports physiotherapist with 15 years of experience. Worked with the Polish BMX national team.')}
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

const ActivitiesPage = () => {
  const location = useLocation();
  const path = location.pathname;
  
  // Determine type from URL path
  if (path.includes('urodziny') || path.includes('birthdays')) return <BirthdaysPage />;
  if (path.includes('fizjoterapia') || path.includes('physio')) return <PhysioPage />;
  if (path.includes('zajecia') || path.includes('classes')) return <ClassesPage />;

  return <ClassesPage />;
};

export default ActivitiesPage;
